import { Property } from '@/integrations/mysql/types';
import { apiJson, normalizeApiArray } from '@/lib/api-client';

export interface SearchFilters {
  empreendimento?: string;
  cidade?: string;
  dataInicio?: string;
  dataFim?: string;
  hospedes?: number;
  tipo?: string;
  tipoServico?: string;
  precoMin?: number;
  precoMax?: number;
  quartos?: number;
  ownerId?: number;
  buscarDatasExatas?: boolean;
  /** Busca de cotas à venda (is_share_for_sale) */
  forSale?: boolean;
  /** Paginação (backend Express): com limit, resposta vira objeto */
  limit?: number;
  cursor?: number | null;
  includeTotal?: boolean;
}

export interface SearchResult {
  property: Property;
  availablePeriods: Array<{
    id: number;
    start_date: string;
    end_date: string;
    price_per_night: number;
    max_guests: number;
    availability_type?: 'troca' | 'aluguel' | 'troca_aluguel';
    notes?: string;
  }>;
  minPrice: number;
  maxPrice: number;
  hasAvailability: boolean;
}

export interface SearchPageResponse {
  items: SearchResult[];
  nextCursor: number | null;
  hasMore: boolean;
  total?: number;
}

function isSearchPageResponse(
  body: unknown,
): body is SearchPageResponse {
  return (
    !!body &&
    typeof body === 'object' &&
    'items' in body &&
    Array.isArray((body as SearchPageResponse).items)
  );
}

export class SearchService {
  /** Busca legada: sem `limit` no body → array direto do backend */
  static async searchProperties(
    filters: SearchFilters,
  ): Promise<SearchResult[]> {
    const { limit, cursor, includeTotal, ...rest } = filters;
    void limit;
    void cursor;
    void includeTotal;
    const res = await apiJson<unknown>('api/properties/search', {
      method: 'POST',
      body: JSON.stringify(rest),
    });
    if (isSearchPageResponse(res)) {
      return res.items;
    }
    return normalizeApiArray<SearchResult>(res);
  }

  /** Busca paginada (scroll infinito na /busca) */
  static async searchPropertiesPage(
    filters: SearchFilters & { limit: number; cursor?: number | null; includeTotal?: boolean },
  ): Promise<SearchPageResponse> {
    const res = await apiJson<unknown>('api/properties/search', {
      method: 'POST',
      body: JSON.stringify(filters),
    });
    if (isSearchPageResponse(res)) {
      return res;
    }
    const items = normalizeApiArray<SearchResult>(res);
    return {
      items,
      nextCursor: null,
      hasMore: false,
      total: items.length,
    };
  }

  static async getAllProperties(): Promise<SearchResult[]> {
    return this.searchProperties({});
  }

  static async searchByCity(city: string): Promise<SearchResult[]> {
    return this.searchProperties({ cidade: city });
  }

  static async searchByGuests(guests: number): Promise<SearchResult[]> {
    return this.searchProperties({ hospedes: guests });
  }
}
